import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth, IMPERSONABLE_ROLES } from '../../context/AuthContext';

const {
  FiEye, FiHeart, FiHome, FiShoppingBag, FiGitBranch, FiBriefcase,
  FiCheck, FiX, FiInfo,
} = FiIcons;

const ICON_MAP = {
  FiEye, FiHeart, FiHome, FiShoppingBag, FiGitBranch, FiBriefcase,
};

const ROLE_CAPABILITIES = {
  visitor: {
    can:    ['Browse public memorials', 'View tributes', 'See public guestbooks', 'Explore marketplace'],
    cannot: ['Leave tributes', 'Sign guestbooks', 'Purchase items', 'Access DocuVault', 'Create memorials'],
  },
  contributor: {
    can:    ['Everything a Visitor can', 'Leave tributes & light candles', 'Sign guestbooks', 'Purchase memorial products', 'RSVP to events'],
    cannot: ['Edit memorial content', 'Moderate tributes', 'Access admin panels', 'Manage DocuVault'],
  },
  memorial_owner: {
    can:    ['Full memorial editing', 'Moderate tributes', 'Manage gallery', 'Invite family members', 'Configure privacy', 'Schedule events'],
    cannot: ['Access other memorials', 'Platform admin features', 'DocuVault (unless subscribed)', 'Merchant portal'],
  },
  merchant: {
    can:    ['List & manage products', 'View sales analytics', 'Request payouts', 'Configure shop settings', 'Manage inventory'],
    cannot: ['Edit memorials', 'Admin features', 'DocuVault', 'See other merchants\' data'],
  },
  digital_heir: {
    can:    ['View designated memorials', 'Claim memorial ownership on trigger', 'View legacy chain', 'Receive notifications'],
    cannot: ['Edit memorial before claim', 'Admin features', 'Merchant portal', 'DocuVault (unless granted)'],
  },
  estate_executor: {
    can:    ['Access DocuVault estate documents', 'Manage asset distribution', 'View expense tracker', 'Coordinate RSVPs', 'Access project management'],
    cannot: ['Admin features', 'Merchant portal', 'Edit public memorial content', 'Access other users\' data'],
  },
};

const ImpersonationPreviewPanel = () => {
  const { isImpersonating, impersonatedRole, stopImpersonation, startImpersonation, permissions } = useAuth();

  if (!permissions.isTrueSuperAdmin()) return null;

  const role = IMPERSONABLE_ROLES.find(r => r.id === impersonatedRole);
  const caps = ROLE_CAPABILITIES[impersonatedRole] || { can: [], cannot: [] };

  if (!isImpersonating) return null;

  return (
    <motion.div initial={{ opacity: 0, x: 320 }} animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 320 }}
      className="fixed right-6 top-24 z-[450] w-80 bg-white rounded-[32px] border-2 border-orange-200
                 shadow-2xl shadow-orange-500/10 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            {role && ICON_MAP[role.icon] && (
              <SafeIcon icon={ICON_MAP[role.icon]} className="w-4 h-4 text-white" />
            )}
            <p className="text-white font-black text-sm">{role?.label}</p>
          </div>
          <button onClick={stopImpersonation}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white/70 hover:text-white">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] text-white/70 font-black uppercase tracking-widest">
          Role Preview Active
        </p>
      </div>

      <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-2xl border border-orange-100">
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-orange-700 font-light leading-relaxed">{role?.desc}</p>
        </div>

        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-green-600 mb-2">✓ Can Do</p>
          <div className="space-y-1.5">
            {caps.can.map((c, i) => (
              <div key={i} className="flex items-start gap-2">
                <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-accent-navy font-light">{c}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-red-500 mb-2">✗ Cannot Do</p>
          <div className="space-y-1.5">
            {caps.cannot.map((c, i) => (
              <div key={i} className="flex items-start gap-2">
                <SafeIcon icon={FiX} className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-text-muted font-light">{c}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2">
            Switch to
          </p>
          <div className="flex flex-wrap gap-1.5">
            {IMPERSONABLE_ROLES.filter(r => r.id !== impersonatedRole).map(r => {
              const IC = ICON_MAP[r.icon];
              return (
                <button key={r.id} onClick={() => startImpersonation(r.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-light border border-brand-dark
                             rounded-xl text-[9px] font-black uppercase tracking-widest text-accent-navy
                             hover:border-accent-gold hover:text-accent-gold transition-all">
                  {IC && <SafeIcon icon={IC} className="w-3 h-3" />}
                  {r.label.replace('Memorial ', '').replace('Estate ', '')}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ImpersonationPreviewPanel;